import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, createContext, useContext, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Phone, Settings, Send, MessageSquare, Users, Calendar, FileText, RefreshCw, ExternalLink, Copy } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { getMyVoysId, updateMyVoysId } from "@/lib/voys.functions";
import { VoysCallButton } from "@/components/voys/VoysCallButton";
import { VoysCallHistory } from "@/components/voys/VoysCallHistory";
import {
  getOfficeSettings,
  updateOfficeSettings,
  officeQuickCustomerSearch,
  officeGetCustomerSummary,
  listAccountantOutbox,
  markOrdersReadyForAccountant,
  sendOrdersToAccountant,
} from "@/lib/office-workspace.functions";
import {
  officeListPlanning,
  officeCreatePlanningEvent,
  officeListEmployees,
} from "@/lib/office.functions";
import { Plus, MapPin, Clock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/medewerker/office")({
  head: () => ({
    meta: [
      { title: "Office — Klantenservice werkplek" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OfficePage,
});

function formatEuro(cents: number | null | undefined) {
  return "€ " + (((cents ?? 0) / 100).toFixed(2).replace(".", ","));
}

// ---------- Geselecteerde klant context ----------
type SelectedCustomer = {
  id: string;
  name: string | null;
  phone: string | null;
  customerNumber: string | null;
} | null;

const SelectedCustomerContext = createContext<{
  customer: SelectedCustomer;
  setCustomer: (c: SelectedCustomer) => void;
}>({ customer: null, setCustomer: () => {} });

const useSelectedCustomer = () => useContext(SelectedCustomerContext);

function phoneDigits(phone: string | null | undefined) {
  return (phone ?? "").replace(/\D/g, "");
}

function OfficePage() {
  const { isAdmin, loading } = useAuth();
  const [customer, setCustomer] = useState<SelectedCustomer>(null);

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Laden…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-2">Geen toegang</h1>
        <p className="text-sm text-muted-foreground">Deze werkplek is alleen voor medewerkers.</p>
      </div>
    );
  }

  const digits = phoneDigits(customer?.phone);
  const whatsappSrc = digits ? `/admin/whatsapp?phone=${encodeURIComponent(digits)}` : "/admin/whatsapp";
  

  return (
    <SelectedCustomerContext.Provider value={{ customer, setCustomer }}>
      <div className="min-h-screen bg-muted/30">
        <header className="sticky top-0 z-20 bg-background border-b px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-semibold">EO</div>
            <div>
              <h1 className="text-base font-semibold leading-tight">Elvion Office</h1>
              <p className="text-[11px] text-muted-foreground leading-tight">Berichten · Bellen · Agenda · Klanten · Facturen</p>
            </div>
          </div>
          <div className="flex-1 min-w-[240px] max-w-xl">
            <TopCustomerSelector />
          </div>
          <div className="flex items-center gap-2">
            <QuickCallInput />
            <OfficeSettingsDialog />
          </div>
        </header>

        <main className="p-3 grid gap-3 grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)]">
          {/* Linker sidebar */}
          <aside className="space-y-3 order-2 xl:order-1">
            <CustomerPanel />
            <RecentCallsPanel />
            <QuickActionsPanel />
          </aside>

          {/* Hoofdpaneel met tabs */}
          <section className="order-1 xl:order-2 bg-background border rounded-lg overflow-hidden flex flex-col" style={{ minHeight: "calc(100vh - 96px)" }}>
            <Tabs defaultValue="whatsapp" className="flex-1 flex flex-col">
              <div className="border-b px-2 py-1.5 flex items-center justify-between gap-2">
                <TabsList className="bg-transparent gap-1">
                  <TabsTrigger value="whatsapp" className="data-[state=active]:bg-muted"><MessageSquare className="w-4 h-4 mr-1.5" /> WhatsApp</TabsTrigger>
                  <TabsTrigger value="agenda" className="data-[state=active]:bg-muted"><Calendar className="w-4 h-4 mr-1.5" /> Agenda</TabsTrigger>
                  <TabsTrigger value="bellen" className="data-[state=active]:bg-muted"><Phone className="w-4 h-4 mr-1.5" /> Bellen</TabsTrigger>
                  <TabsTrigger value="outbox" className="data-[state=active]:bg-muted"><FileText className="w-4 h-4 mr-1.5" /> Boekhouder</TabsTrigger>
                  <TabsTrigger value="sent" className="data-[state=active]:bg-muted">Verzonden</TabsTrigger>
                </TabsList>
                {customer && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 pr-1">
                    <span>Actief:</span>
                    <Badge variant="secondary" className="text-[10px]">
                      {customer.name || "(zonder naam)"}{customer.phone ? ` · ${customer.phone}` : ""}
                    </Badge>
                    <button onClick={() => setCustomer(null)} className="underline hover:text-foreground">wissen</button>
                  </div>
                )}
              </div>

              <TabsContent value="whatsapp" className="flex-1 overflow-hidden m-0 relative">
                <div className="absolute top-2 right-3 z-10">
                  <a href={whatsappSrc} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 bg-background/80 backdrop-blur px-2 py-1 rounded border">
                    Vol scherm <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <iframe key={whatsappSrc} src={whatsappSrc} className="w-full h-full border-0" title="WhatsApp" />
              </TabsContent>

              <TabsContent value="agenda" className="flex-1 overflow-auto m-0 p-4">
                <AgendaPanel />
              </TabsContent>


              <TabsContent value="bellen" className="flex-1 overflow-auto m-0 p-4">
                <BellenPanel />
              </TabsContent>

              <TabsContent value="outbox" className="flex-1 overflow-auto p-3 m-0">
                <AccountantOutbox status="ready" />
              </TabsContent>
              <TabsContent value="sent" className="flex-1 overflow-auto p-3 m-0">
                <AccountantOutbox status="sent" />
              </TabsContent>
            </Tabs>
          </section>
        </main>
      </div>
    </SelectedCustomerContext.Provider>
  );
}


// ---------- Klantkiezer bovenaan ----------
function TopCustomerSelector() {
  const { customer, setCustomer } = useSelectedCustomer();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const searchFn = useServerFn(officeQuickCustomerSearch);

  const { data: search } = useQuery({
    queryKey: ["office-top-search", q],
    queryFn: () => searchFn({ data: { query: q } }),
    enabled: q.trim().length >= 2,
  });

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-muted-foreground shrink-0" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={customer ? `Klant: ${customer.name || "(zonder naam)"}${customer.phone ? " · " + customer.phone : ""}` : "Zoek klant om WhatsApp, agenda & bellen automatisch te laden…"}
          className="h-9 text-sm"
        />
        {customer && (
          <Button size="sm" variant="ghost" onClick={() => { setCustomer(null); setQ(""); }} className="h-8 shrink-0">
            Wissen
          </Button>
        )}
      </div>
      {open && q.length >= 2 && (
        <ul className="absolute z-30 top-full left-0 right-0 mt-1 max-h-64 overflow-auto bg-background border rounded shadow text-xs divide-y">
          {(search?.customers ?? []).map((c: any) => (
            <li key={c.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setCustomer({
                    id: c.id,
                    name: c.full_name ?? null,
                    phone: c.phone ?? null,
                    customerNumber: c.customer_number ?? null,
                  });
                  setQ("");
                  setOpen(false);
                }}
                className="w-full text-left px-2 py-1.5 hover:bg-muted"
              >
                <div className="font-medium truncate">{c.full_name || "(zonder naam)"}</div>
                <div className="text-[10px] text-muted-foreground truncate">{c.customer_number || "geen nr"} · {c.phone || "geen tel"}</div>
              </button>
            </li>
          ))}
          {!search?.customers?.length && <li className="px-2 py-1.5 text-muted-foreground">Geen resultaten</li>}
        </ul>
      )}
    </div>
  );
}

// ---------- Agenda paneel (planning + klantbestellingen) ----------
function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function NewPlanningDialog({ onCreated }: { onCreated: () => void }) {
  const { customer } = useSelectedCustomer();
  const [open, setOpen] = useState(false);
  const createFn = useServerFn(officeCreatePlanningEvent);
  const listEmpFn = useServerFn(officeListEmployees);

  const { data: emp } = useQuery({
    queryKey: ["office-employees"],
    queryFn: () => listEmpFn({}),
    enabled: open,
  });

  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return d;
  }, [open]);
  const defaultEnd = useMemo(() => new Date(defaultStart.getTime() + 60 * 60 * 1000), [defaultStart]);

  const [title, setTitle] = useState("");
  const [type, setType] = useState("installation");
  const [startsAt, setStartsAt] = useState(toLocalInput(defaultStart));
  const [endsAt, setEndsAt] = useState(toLocalInput(defaultEnd));
  const [location, setLocation] = useState("");
  const [assignedTo, setAssignedTo] = useState<string>("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setStartsAt(toLocalInput(defaultStart));
      setEndsAt(toLocalInput(defaultEnd));
      setTitle(customer?.name ? `Afspraak ${customer.name}` : "");
    }
  }, [open, customer?.id, customer?.name, defaultStart, defaultEnd]);

  const createMut = useMutation({
    mutationFn: (force: boolean) =>
      createFn({
        data: {
          title: title.trim() || "Nieuwe afspraak",
          starts_at: new Date(startsAt).toISOString(),
          ends_at: new Date(endsAt).toISOString(),
          type,
          location: location.trim() || null,
          notes: notes.trim() || null,
          customer_id: customer?.id ?? null,
          assigned_to: assignedTo || null,
          force,
        },
      }),
    onSuccess: () => {
      toast.success("Afspraak opgeslagen");
      setOpen(false);
      onCreated();
    },
    onError: (e: any) => {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.startsWith("PLANNING_CONFLICT:")) {
        if (window.confirm("Er is een agenda-conflict. Toch inplannen?")) {
          createMut.mutate(true);
          return;
        }
      } else {
        toast.error(msg || "Opslaan mislukt");
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="w-4 h-4 mr-1" /> Nieuwe afspraak</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nieuwe agenda-afspraak</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {customer ? (
            <div className="text-xs bg-muted rounded px-2 py-1.5">
              Gekoppeld aan klant: <span className="font-medium">{customer.name || "(zonder naam)"}</span>
              {customer.customerNumber ? ` · ${customer.customerNumber}` : ""}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Geen klant geselecteerd — selecteer bovenaan om te koppelen.</div>
          )}
          <div>
            <Label className="text-xs">Titel</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Montage groepenkast" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Start</Label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Einde</Label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Type</Label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full h-9 border rounded px-2 text-sm bg-background"
              >
                <option value="installation">Installatie</option>
                <option value="inspection">Inspectie</option>
                <option value="meeting">Bespreking</option>
                <option value="service">Service</option>
                <option value="other">Overig</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Medewerker</Label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full h-9 border rounded px-2 text-sm bg-background"
              >
                <option value="">— geen —</option>
                {(emp?.employees ?? []).map((u: any) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Locatie</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Adres of plaats" />
          </div>
          <div>
            <Label className="text-xs">Notities</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button
            onClick={() => createMut.mutate(false)}
            disabled={createMut.isPending || !startsAt || !endsAt}
          >
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgendaPanel() {
  const { customer } = useSelectedCustomer();
  const qc = useQueryClient();
  const listFn = useServerFn(officeListPlanning);
  const summaryFn = useServerFn(officeGetCustomerSummary);

  const range = useMemo(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date();
    to.setDate(to.getDate() + 60);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const { data: planning, isLoading } = useQuery({
    queryKey: ["office-planning", range.from, range.to],
    queryFn: () => listFn({ data: range }),
  });

  const { data: summary } = useQuery({
    queryKey: ["office-summary-agenda", customer?.id],
    queryFn: () => summaryFn({ data: { userId: customer!.id } }),
    enabled: !!customer?.id,
  });

  const events = (planning?.events ?? []) as any[];
  const filtered = customer ? events.filter((e) => e.customer_id === customer.id) : events;

  const refresh = () => qc.invalidateQueries({ queryKey: ["office-planning"] });

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">
            Agenda {customer ? `— ${customer.name || "(zonder naam)"}` : ""}
          </h2>
          <p className="text-xs text-muted-foreground">
            {customer ? "Afspraken en bestellingen van deze klant" : "Alle afspraken (komende 60 dagen)"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={refresh}><RefreshCw className="w-3.5 h-3.5" /></Button>
          <NewPlanningDialog onCreated={refresh} />
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4" /> Afspraken</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <div className="text-xs text-muted-foreground">Laden…</div>}
          {!isLoading && filtered.length === 0 && (
            <div className="text-xs text-muted-foreground">
              {customer ? "Nog geen afspraken voor deze klant." : "Geen aankomende afspraken."}
            </div>
          )}
          {filtered.length > 0 && (
            <ul className="divide-y text-sm">
              {filtered.map((e) => {
                const s = new Date(e.starts_at);
                const en = new Date(e.ends_at);
                return (
                  <li key={e.id} className="py-2 flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ backgroundColor: e.color || "#3b82f6" }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{e.title}</div>
                      <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />
                          {s.toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })} – {en.toLocaleTimeString("nl-NL", { timeStyle: "short" })}
                        </span>
                        {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                        <Badge variant="outline" className="text-[10px]">{e.type}</Badge>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {customer && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Bestellingen van deze klant</CardTitle>
          </CardHeader>
          <CardContent>
            {(summary?.orders?.length ?? 0) === 0 && (
              <div className="text-xs text-muted-foreground">Geen bestellingen.</div>
            )}
            {(summary?.orders ?? []).length > 0 && (
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="p-2">Nummer</th>
                    <th className="p-2">Datum</th>
                    <th className="p-2">Status</th>
                    <th className="p-2 text-right">Totaal</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(summary?.orders ?? []).map((o: any) => (
                    <tr key={o.id} className="border-t">
                      <td className="p-2 font-mono">{o.order_number}</td>
                      <td className="p-2">{o.created_at ? new Date(o.created_at).toLocaleDateString("nl-NL") : ""}</td>
                      <td className="p-2">{o.status}</td>
                      <td className="p-2 text-right">{formatEuro(o.total_cents)}</td>
                      <td className="p-2 text-right">
                        <Link to="/account/bestellingen/$id" params={{ id: o.id }} target="_blank" className="text-primary hover:underline">Openen</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ---------- Bellen paneel ----------
function BellenPanel() {
  const { customer } = useSelectedCustomer();
  const [number, setNumber] = useState("");

  // Prefill met geselecteerde klant
  useEffect(() => {
    if (customer?.phone) setNumber(customer.phone);
  }, [customer?.id, customer?.phone]);
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Phone className="w-4 h-4" /> Nieuw gesprek starten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="+31 6 12 34 56 78"
              className="flex-1"
            />
            <VoysCallButton phone={number} threadKey={number.replace(/\D/g, "")} variant="default" label="Bel via Voys" />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Voer een nummer in en klik op bellen. Het gesprek wordt in je Voys-toestel geopend en toegevoegd aan de geschiedenis.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Phone className="w-4 h-4" />
            {customer?.phone ? `Gesprekken met ${customer.name || customer.phone}` : "Recente gesprekken"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <VoysCallHistory
            phone={customer?.phone ?? undefined}
            threadKey={customer?.phone ? phoneDigits(customer.phone) : undefined}
            limit={25}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Snelbel-invoer ----------
function QuickCallInput() {
  const [number, setNumber] = useState("");
  return (
    <div className="flex items-center gap-1">
      <Input
        value={number}
        onChange={(e) => setNumber(e.target.value)}
        placeholder="+31 6…"
        className="h-8 w-40 text-xs"
      />
      <VoysCallButton
        phone={number}
        variant="secondary"
        size="sm"
        label="Bel"
      />
    </div>
  );
}


// ---------- Klantpaneel ----------
function CustomerPanel() {
  const { customer, setCustomer } = useSelectedCustomer();
  const [q, setQ] = useState("");
  const selectedId = customer?.id ?? null;
  const searchFn = useServerFn(officeQuickCustomerSearch);
  const summaryFn = useServerFn(officeGetCustomerSummary);



  const { data: search } = useQuery({
    queryKey: ["office-search", q],
    queryFn: () => searchFn({ data: { query: q } }),
    enabled: q.trim().length >= 2,
  });
  const { data: summary } = useQuery({
    queryKey: ["office-summary", selectedId],
    queryFn: () => summaryFn({ data: { userId: selectedId! } }),
    enabled: !!selectedId,
  });

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Klant zoeken</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Naam, klantnummer of nummer" className="h-8 text-xs" />
        {q.length >= 2 && (
          <ul className="max-h-40 overflow-auto text-xs border rounded divide-y">
            {(search?.customers ?? []).map((c: any) => (
              <li key={c.id}>
                <button
                  onClick={() => setCustomer({
                    id: c.id,
                    name: c.full_name ?? null,
                    phone: c.phone ?? null,
                    customerNumber: c.customer_number ?? null,
                  })}
                  className={`w-full text-left px-2 py-1.5 hover:bg-muted ${selectedId === c.id ? "bg-muted" : ""}`}
                >
                  <div className="font-medium truncate">{c.full_name || "(zonder naam)"}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{c.customer_number || "geen nr"} · {c.phone || "geen tel"}</div>
                </button>
              </li>
            ))}
            {!search?.customers?.length && <li className="px-2 py-1.5 text-muted-foreground">Geen resultaten</li>}
          </ul>
        )}

        {summary?.profile && (
          <div className="border rounded p-2 text-xs space-y-1.5">
            <div className="font-semibold">{summary.profile.full_name || "(zonder naam)"}</div>
            <div className="text-muted-foreground text-[11px]">{summary.profile.email} · {summary.profile.customer_number}</div>
            {summary.profile.phone && (
              <>
                {(() => {
                  const phone = summary.profile!.phone!;
                  return (
                    <div className="flex items-center gap-2">
                      <a href={`tel:+${phone.replace(/\D/g, "")}`} className="underline">{phone}</a>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(phone);
                          toast.success("Telefoonnummer gekopieerd");
                        }}
                        className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground border rounded px-1.5 py-0.5 hover:bg-muted transition-colors"
                        title="Kopieer nummer"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <VoysCallButton
                        phone={phone}
                        threadKey={phone.replace(/\D/g, "")}
                        size="xs"
                        label="Voys"
                      />
                    </div>
                  );
                })()}
                <VoysCallHistory
                  phone={summary.profile.phone}
                  threadKey={summary.profile.phone.replace(/\D/g, "")}
                  limit={5}
                  className="pt-1"
                />
              </>
            )}
            <div className="pt-1">
              <div className="text-[10px] uppercase text-muted-foreground">Laatste orders</div>
              {summary.orders.length === 0 && <div className="text-muted-foreground">Geen</div>}
              {summary.orders.map((o: any) => (
                <div key={o.id} className="flex justify-between border-b py-0.5">
                  <span>{o.order_number}</span>
                  <span>{formatEuro(o.total_cents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Recente belactiviteit (globaal) ----------
function RecentCallsPanel() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Phone className="w-4 h-4" /> Mijn recente Voys-oproepen
        </CardTitle>
      </CardHeader>
      <CardContent>
        <VoysCallHistory limit={10} />
      </CardContent>
    </Card>
  );
}

// ---------- Quick actions ----------
function QuickActionsPanel() {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Snelacties</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-2 gap-2 text-xs">
        <Link to="/admin/facturen" className="border rounded p-2 hover:bg-muted flex flex-col items-start">
          <FileText className="w-4 h-4 mb-1" /> Nieuwe factuur
        </Link>
        <Link to="/admin/klanten" className="border rounded p-2 hover:bg-muted flex flex-col items-start">
          <Users className="w-4 h-4 mb-1" /> Nieuwe klant
        </Link>
      </CardContent>
    </Card>
  );
}

// ---------- Accountant outbox ----------
function AccountantOutbox({ status }: { status: "ready" | "sent" }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listAccountantOutbox);
  const markFn = useServerFn(markOrdersReadyForAccountant);
  const sendFn = useServerFn(sendOrdersToAccountant);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["accountant-outbox", status],
    queryFn: () => listFn({ data: { status, limit: 100 } }),
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const orders = data?.orders ?? [];
  const totalSelected = useMemo(
    () => orders.filter((o) => selected.has(o.id)).reduce((s, o) => s + (o.total_cents ?? 0), 0),
    [orders, selected],
  );

  const sendMut = useMutation({
    mutationFn: (ids: string[]) => sendFn({ data: { orderIds: ids } }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(`${r.count} facturen verstuurd naar ${r.accountantEmail}`);
        setSelected(new Set());
        qc.invalidateQueries({ queryKey: ["accountant-outbox"] });
      } else {
        toast.error(r.detail || r.reason || "Versturen mislukt");
      }
    },
  });

  const markMut = useMutation({
    mutationFn: (ids: string[]) => markFn({ data: { orderIds: ids, status: status === "ready" ? "draft" : "ready" } }),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["accountant-outbox"] });
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm">
          {status === "ready" ? "Klaar voor de boekhouder" : "Al verstuurd"} · {orders.length} facturen
          {selected.size > 0 && <> · <span className="font-medium">{selected.size} geselecteerd ({formatEuro(totalSelected)})</span></>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => refetch()}><RefreshCw className="w-3.5 h-3.5" /></Button>
          {status === "ready" && (
            <Button
              size="sm"
              disabled={selected.size === 0 || sendMut.isPending}
              onClick={() => sendMut.mutate(Array.from(selected))}
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Verstuur naar boekhouder
            </Button>
          )}
          {selected.size > 0 && (
            <Button size="sm" variant="outline" onClick={() => markMut.mutate(Array.from(selected))}>
              {status === "ready" ? "Terug naar concept" : "Opnieuw klaarzetten"}
            </Button>
          )}
        </div>
      </div>

      {isLoading && <div className="text-xs text-muted-foreground">Laden…</div>}

      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-2 w-8"></th>
              <th className="p-2">Factuur</th>
              <th className="p-2">Datum</th>
              <th className="p-2">Klant</th>
              <th className="p-2 text-right">Totaal</th>
              <th className="p-2">Status</th>
              {status === "sent" && <th className="p-2">Verstuurd</th>}
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t">
                <td className="p-2">
                  <Checkbox
                    checked={selected.has(o.id)}
                    onCheckedChange={(v) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (v) next.add(o.id); else next.delete(o.id);
                        return next;
                      });
                    }}
                  />
                </td>
                <td className="p-2 font-mono">{o.order_number}</td>
                <td className="p-2">{new Date(o.created_at).toLocaleDateString("nl-NL")}</td>
                <td className="p-2">{o.customer_name || "—"} <span className="text-muted-foreground">{o.customer_number || ""}</span></td>
                <td className="p-2 text-right">{formatEuro(o.total_cents)}</td>
                <td className="p-2"><Badge variant="outline" className="text-[10px]">{o.payment_status || o.status}</Badge></td>
                {status === "sent" && <td className="p-2">{o.sent_to_accountant_at ? new Date(o.sent_to_accountant_at).toLocaleDateString("nl-NL") : ""}</td>}
              </tr>
            ))}
            {!orders.length && !isLoading && (
              <tr><td colSpan={status === "sent" ? 7 : 6} className="p-4 text-center text-muted-foreground">Geen facturen</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-muted-foreground">
        💡 Om facturen hier te laten verschijnen: markeer ze in <Link to="/admin/facturen" className="underline">/admin/facturen</Link> als 'klaar voor boekhouder' (of gebruik straks de knop in de klantpaneel-flow).
      </p>
    </div>
  );
}

// ---------- Instellingen ----------
function OfficeSettingsDialog() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const getFn = useServerFn(getOfficeSettings);
  const updateFn = useServerFn(updateOfficeSettings);
  const getVoysFn = useServerFn(getMyVoysId);
  const updateVoysFn = useServerFn(updateMyVoysId);

  const { data: settings } = useQuery({ queryKey: ["office-settings"], queryFn: () => getFn({}), enabled: open });
  const { data: voys } = useQuery({ queryKey: ["my-voys-id"], queryFn: () => getVoysFn({}), enabled: open });

  const [email, setEmail] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [voysId, setVoysId] = useState("");

  // Hydrate on open
  useMemo(() => {
    if (settings) {
      setEmail(settings.accountant_email ?? "");
      setCc(settings.accountant_cc ?? "");
      setSubject(settings.email_subject_template ?? "Facturen {{month}} {{year}} — Elvion Group Nederland");
    }
    if (voys) setVoysId(voys.voysUserId ?? "");
  }, [settings, voys]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await updateFn({
        data: {
          accountant_email: email || null,
          accountant_cc: cc || null,
          email_subject_template: subject,
        },
      });
      await updateVoysFn({ data: { voysUserId: voysId || null } });
    },
    onSuccess: () => {
      toast.success("Instellingen opgeslagen");
      qc.invalidateQueries({ queryKey: ["office-settings"] });
      qc.invalidateQueries({ queryKey: ["my-voys-id"] });
      setOpen(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Fout bij opslaan"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline"><Settings className="w-4 h-4 mr-1" /> Instellingen</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Office-instellingen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">Boekhouder</div>
            <Label className="text-xs">E-mailadres</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="boekhouder@voorbeeld.nl" />
            <Label className="text-xs">CC (komma-gescheiden, optioneel)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="admin@elvion-group.nl" />
            <Label className="text-xs">Onderwerp-sjabloon</Label>
            <Textarea value={subject} onChange={(e) => setSubject(e.target.value)} rows={2} />
            <p className="text-[11px] text-muted-foreground">Beschikbaar: <code>{"{{month}}"}</code> · <code>{"{{year}}"}</code></p>
          </div>

          <div className="space-y-2 border-t pt-3">
            <div className="text-sm font-medium">Voys click-to-dial</div>
            <Label className="text-xs">Jouw Voys-gebruikers-id / interne nummer</Label>
            <Input value={voysId} onChange={(e) => setVoysId(e.target.value)} placeholder="bv. 201 of user-id uit Freedom" />
            <p className="text-[11px] text-muted-foreground">
              {voys?.configured ? "✓ Voys API-token is geconfigureerd." : "⚠ VOYS_API_TOKEN ontbreekt — vraag de beheerder deze toe te voegen."}
              {" "}Zonder Voys-id gebruikt de bel-knop een gewone tel:-link.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
